import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../api/api';
import { toast } from '../hooks/use-toast';
import { Crosshair, ArrowLeft, ArrowSquareOut, MapPin, Fire, LockSimple, LockSimpleOpen, Coins } from '@phosphor-icons/react';

const QUALITY_STYLES = {
  HOT: 'bg-red-100 text-red-700', WARM: 'bg-amber-100 text-amber-800',
  COLD: 'bg-blue-100 text-blue-700',
};
const STATUS_ACTIONS = ['CONTACTED', 'RESPONDED', 'QUOTING', 'WON', 'LOST'];

const ContractorHunterLeads = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState(null);
  const [balance, setBalance] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const [r, b] = await Promise.all([
        axiosInstance.get('/leadhunter/my-leads'),
        axiosInstance.get('/credits/balance').catch(() => null),
      ]);
      setLeads(r.data.leads || []);
      if (b?.data) setBalance(b.data.balance);
    } catch (e) {
      if (e.response?.status === 403) navigate('/contractor-dashboard');
      else setLeads([]);
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const unlock = async (l) => {
    setBusy(`unlock-${l.id}`);
    try {
      const r = await axiosInstance.post(`/leadhunter/my-leads/${l.id}/unlock`);
      setBalance(r.data.balance);
      toast({ title: `Lead unlocked — ${r.data.charged} credits used` });
      load();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Unlock failed';
      toast({ title: 'Not enough credits', description: msg, variant: 'destructive' });
      if (e.response?.status === 402) setTimeout(() => navigate('/leads-marketplace'), 1800);
    } finally { setBusy(null); }
  };

  const setStatus = async (id, status) => {
    setBusy(`${id}-${status}`);
    try {
      await axiosInstance.patch(`/leadhunter/my-leads/${id}`, { status });
      toast({ title: `Lead marked ${status}` });
      load();
    } catch (e) {
      toast({ title: 'Update failed', description: e.response?.data?.detail, variant: 'destructive' });
    } finally { setBusy(null); }
  };

  const fmtVal = (l) => (l.est_value_min && l.est_value_max)
    ? `$${(l.est_value_min / 1000).toFixed(0)}k–$${(l.est_value_max / 1000).toFixed(0)}k` : null;

  return (
    <div className="min-h-screen bg-neutral-100 dark:bg-neutral-900" data-testid="contractor-hunter-leads-page">
      <div className="bg-neutral-900 border-b-4 border-[#D95800]">
        <div className="max-w-5xl mx-auto px-4 py-6 flex items-center gap-4">
          <button onClick={() => navigate('/contractor-dashboard')} className="text-white hover:text-[#D95800]" data-testid="hunter-leads-back-btn">
            <ArrowLeft size={24} weight="bold" />
          </button>
          <div className="w-12 h-12 bg-[#D95800] border-2 border-white flex items-center justify-center">
            <Crosshair size={26} weight="fill" className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-white uppercase">My Hunter Leads</h1>
            <p className="text-neutral-400 text-sm">Homeowner requests our AI found across the web, matched to your trade & area</p>
          </div>
          <div className="ml-auto flex items-center gap-3">
            {balance !== null && (
              <span className="flex items-center gap-1.5 px-3 py-1.5 bg-neutral-800 border border-neutral-600 text-white text-sm font-black rounded" data-testid="hunter-credit-balance">
                <Coins size={16} weight="fill" className="text-amber-400" /> {balance} credits
              </span>
            )}
            <button onClick={() => navigate('/leads-marketplace')}
              className="px-3 py-1.5 bg-[#D95800] text-white text-sm font-black border-2 border-white hover:bg-[#E55D00]"
              data-testid="buy-credits-link">
              Buy Credits
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-4">
        {leads === null && <p className="text-center text-neutral-400 font-bold py-12">Loading…</p>}
        {leads?.length === 0 && (
          <div className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 p-10 text-center shadow-[4px_4px_0px_#000]" data-testid="no-hunter-leads">
            <Crosshair size={40} className="mx-auto text-neutral-300 mb-3" />
            <p className="font-black uppercase">No leads assigned yet</p>
            <p className="text-sm text-neutral-500 mt-1">When our AI Lead Hunter finds homeowner requests matching your trade and area, they'll appear here.</p>
          </div>
        )}
        {leads?.map((l) => (
          <div key={l.id} className="bg-white dark:bg-neutral-800 border-2 border-black dark:border-neutral-700 p-5 shadow-[4px_4px_0px_#000]" data-testid={`hunter-lead-card-${l.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 text-xs font-black rounded ${QUALITY_STYLES[l.quality] || ''}`}>
                    {l.quality === 'HOT' && <Fire size={12} weight="fill" className="inline mr-0.5 -mt-0.5" />}{l.quality}
                  </span>
                  <span className="px-2 py-0.5 text-xs font-black bg-neutral-100 dark:bg-neutral-700 rounded">{l.status}</span>
                  {fmtVal(l) && <span className="px-2 py-0.5 text-xs font-black bg-emerald-100 text-emerald-700 rounded">Est. {fmtVal(l)}</span>}
                </div>
                <h2 className="font-black uppercase mt-2 dark:text-white">{l.project_type || l.trade}</h2>
                <p className="text-sm text-neutral-500 flex items-center gap-1">
                  <MapPin size={14} /> {[l.city, l.state].filter(Boolean).join(', ') || 'Location unknown'} · {l.source}
                </p>
                <p className="text-sm mt-2 dark:text-neutral-300">{l.summary}</p>
                {l.unlocked && l.contact_info && (
                  <p className="text-sm mt-2 font-bold text-[#D95800]" data-testid={`lead-contact-${l.id}`}>Contact: {l.contact_info}</p>
                )}
                {!l.unlocked && (
                  <div className="mt-3 flex items-center gap-3 bg-neutral-50 dark:bg-neutral-700/50 border-2 border-dashed border-neutral-300 dark:border-neutral-600 p-3" data-testid={`lead-locked-${l.id}`}>
                    <LockSimple size={20} weight="fill" className="text-neutral-400 shrink-0" />
                    <p className="text-xs text-neutral-500 dark:text-neutral-400 flex-1">
                      {l.has_contact_info ? 'Contact details, ' : ''}original post link & full request are locked
                    </p>
                    <button onClick={() => unlock(l)} disabled={busy === `unlock-${l.id}`}
                      className="h-9 px-4 flex items-center gap-1.5 text-xs font-black bg-[#D95800] text-white border-2 border-black shadow-[2px_2px_0px_#000] hover:bg-[#E55D00] shrink-0"
                      data-testid={`unlock-lead-btn-${l.id}`}>
                      <LockSimpleOpen size={14} weight="bold" />
                      {busy === `unlock-${l.id}` ? 'Unlocking…' : `Unlock · ${l.unlock_price} credits`}
                    </button>
                  </div>
                )}
              </div>
              <div className="text-center shrink-0">
                <p className="text-3xl font-black text-[#D95800]">{l.score}</p>
                <p className="text-[10px] font-black uppercase text-neutral-400">Score</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-neutral-200 dark:border-neutral-700">
              {l.unlocked && (
                <a href={l.source_urls?.[0]} target="_blank" rel="noreferrer"
                  className="h-9 px-3 flex items-center gap-1.5 text-xs font-bold bg-neutral-900 text-white border-2 border-black"
                  data-testid={`open-lead-source-${l.id}`}>
                  <ArrowSquareOut size={14} /> View Original Post
                </a>
              )}
              {STATUS_ACTIONS.map((s) => (
                <button key={s} onClick={() => setStatus(l.id, s)} disabled={busy === `${l.id}-${s}` || l.status === s}
                  className={`h-9 px-3 text-xs font-bold border-2 ${l.status === s ? 'bg-[#D95800] text-white border-black' : 'bg-neutral-100 dark:bg-neutral-700 dark:text-neutral-200 hover:bg-neutral-200 border-neutral-300 dark:border-neutral-600'}`}
                  data-testid={`lead-status-${s.toLowerCase()}-${l.id}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ContractorHunterLeads;
